import React, { useRef, useState, useEffect } from 'react';
import { Check, Trash2 } from 'lucide-react';
import { Task } from '@/context/TaskContext';
import TaskCard from './TaskCard';
import { useIsMobile } from '@/hooks/use-mobile';

interface SwipeableTaskCardProps {
  task: Task;
  onSelect: (id: string) => void;
  isActive: boolean;
  compact?: boolean;
  onDelete?: (id: string) => void;
  onMarkDone?: (id: string) => void;
  isDragging?: boolean;
}

const SwipeableTaskCard: React.FC<SwipeableTaskCardProps> = ({
  task,
  onSelect,
  isActive,
  compact = false,
  onDelete,
  onMarkDone,
  isDragging = false,
}) => {
  const isMobile = useIsMobile();
  const cardRef = useRef<HTMLDivElement>(null);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Reset position when the component unmounts or task changes, or when dragging starts
  useEffect(() => {
    if (cardRef.current && (isDragging || !task.id)) {
      cardRef.current.style.transform = 'translateX(0)';
      setIsOpen(false);
    }
  }, [task.id, isDragging]);

  // Disable swipe functionality on mobile or when dragging
  if (isMobile || isDragging) {
    return (
      <TaskCard 
        task={task} 
        onSelect={onSelect} 
        isActive={isActive} 
        compact={compact} 
      />
    );
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    // Don't handle swipe if dragging
    if (isDragging) return;
    
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Don't handle swipe if dragging
    if (isDragging) return;
    
    setCurrentX(e.touches[0].clientX);
    const deltaX = currentX - startX;
    
    // Only allow swiping left or resetting position
    if (deltaX < 0) {
      if (cardRef.current) {
        // Limit how far the card can be swiped
        const newX = Math.max(deltaX, -160);
        cardRef.current.style.transform = `translateX(${newX}px)`;
      }
    } else if (isOpen && deltaX > 0) {
      // Allow swiping right to close if already open
      if (cardRef.current) {
        const newX = Math.min(deltaX - 160, 0);
        cardRef.current.style.transform = `translateX(${newX}px)`;
      }
    }
  };

  const handleTouchEnd = () => {
    // Don't handle swipe if dragging
    if (isDragging) return;
    
    const deltaX = currentX - startX;
    
    // If swiped far enough to the left, snap open
    if (deltaX < -50 && !isOpen) {
      if (cardRef.current) {
        cardRef.current.style.transform = 'translateX(-160px)';
        setIsOpen(true);
      }
    } 
    // If swiped to the right when open, snap closed
    else if (deltaX > 50 && isOpen) {
      if (cardRef.current) {
        cardRef.current.style.transform = 'translateX(0)';
        setIsOpen(false);
      }
    } 
    // Otherwise return to original state
    else {
      if (cardRef.current) {
        cardRef.current.style.transform = isOpen ? 'translateX(-160px)' : 'translateX(0)';
      }
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(task.id);
    }
    setIsOpen(false);
  };

  const handleMarkDone = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkDone) {
      onMarkDone(task.id);
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative overflow-visible ${isDragging ? 'pointer-events-none' : ''}`}>
      {/* Action buttons - keep overflow hidden only here */}
      <div className="absolute top-0 right-0 bottom-0 flex h-full overflow-hidden rounded-r-lg">
        <button 
          onClick={handleMarkDone}
          className="bg-blue-500 text-white flex items-center justify-center w-[80px] h-full"
          disabled={isDragging}
        >
          <Check size={20} />
        </button>
        <button 
          onClick={handleDelete}
          className="bg-red-500 text-white flex items-center justify-center w-[80px] h-full"
          disabled={isDragging}
        >
          <Trash2 size={20} />
        </button>
      </div>
      
      {/* Task card */}
      <div 
        ref={cardRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`transform transition-transform duration-300 ease-out rounded-lg overflow-hidden ${isDragging ? 'transition-none' : ''}`}
        style={{
          // Reset transform when dragging to avoid conflicts
          transform: isDragging ? 'translateX(0)' : undefined,
        }}
      >
        <TaskCard 
          task={task} 
          onSelect={onSelect} 
          isActive={isActive} 
          compact={compact} 
        />
      </div>
    </div>
  );
};

export default SwipeableTaskCard;
